/**
 * @file MobileStories.tsx
 * @purpose "Stories" style walkthrough for mobile devices, presented as a bottom sheet
 * to allow users to see the underlying app context.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Check, Sparkles, Calendar, BarChart3, Target, HelpCircle, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWalkthroughAnalytics } from '@/hooks/useWalkthroughAnalytics';

interface MobileStoriesProps {
    onComplete: () => void;
}

interface StorySlide {
    id: string;
    targetId?: string;
    route?: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    content?: React.ReactNode;
    bgGradient: string;
}

const SLIDES: StorySlide[] = [
    {
        id: 'welcome',
        targetId: 'interaction-hub',
        route: '/dashboard',
        title: 'Welcome to Your HQ',
        description: 'This is your mission control. We built a transparent engine that shows you exactly why every workout is prescribed.',
        icon: <Sparkles className="w-8 h-8" />,
        bgGradient: 'from-blue-600 via-indigo-600 to-violet-600',
    },
    {
        id: 'today_card',
        targetId: 'today-workout-card',
        route: '/dashboard',
        title: 'Your Daily Mission',
        description: 'Your most critical card. It shows today\'s workout with precise targets. Click to see the science behind it.',
        icon: <Target className="w-8 h-8" />,
        bgGradient: 'from-emerald-600 via-teal-600 to-cyan-600',
    },
    {
        id: 'connect_intervals',
        targetId: 'intervals-connect-card',
        route: '/dashboard',
        title: 'Connect Intervals.icu',
        description: 'Send workouts to Intervals.icu so Garmin, Wahoo, Zwift and other devices can pick them up. Completed training comes back here for better plan updates.',
        icon: <Link2 className="w-8 h-8" />,
        bgGradient: 'from-sky-600 via-blue-600 to-indigo-600',
    },
    {
        id: 'weekly_stats',
        targetId: 'upcoming-workouts',
        route: '/dashboard',
        title: 'The Feedback Loop',
        description: 'We visualize your progress in real-time. Watch your load distribution here. Staying in the "Green Zone" prevents injury.',
        icon: <BarChart3 className="w-8 h-8" />,
        bgGradient: 'from-amber-500 via-orange-500 to-red-500',
    },
    {
        id: 'schedule_nav',
        targetId: 'nav-item-/schedule',
        route: '/dashboard',
        title: 'The Master Plan',
        description: 'Tap the Schedule tab to see your season broken into Blocks (Base, Build, Peak). You can also drag and drop workouts there.',
        icon: <Calendar className="w-8 h-8" />,
        bgGradient: 'from-purple-600 via-pink-500 to-rose-500',
    },
    {
        id: 'support',
        targetId: 'interaction-hub',
        route: '/dashboard',
        title: 'Need Help? We\'re Here!',
        description: 'Look for the help button in the bottom-right corner. Tap it anytime for support, to share feedback, or to restart this guide.',
        icon: <HelpCircle className="w-8 h-8" />,
        bgGradient: 'from-indigo-500 via-blue-500 to-cyan-500',
    },
    {
        id: 'ready',
        title: 'You Are Ready',
        description: 'Remember: Consistency beats intensity. Trust the process, and we\'ll handle the math.',
        icon: <Check className="w-8 h-8" />,
        bgGradient: 'from-green-500 via-emerald-500 to-teal-500',
    },
];

export function MobileStories({ onComplete }: MobileStoriesProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [direction, setDirection] = useState(0);
    const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
    const { logEvent } = useWalkthroughAnalytics();
    const isLastSlide = currentSlide === SLIDES.length - 1;
    const navigate = useNavigate();
    const location = useLocation();

    const slide = SLIDES[currentSlide];

    // Handle Spotlight and Navigation
    useEffect(() => {
        logEvent({ eventType: 'view_slide', stepId: slide.id, platform: 'mobile' });

        if (slide.route && location.pathname !== slide.route) {
            navigate(slide.route);
            // Give extra time for navigation
            setTimeout(() => updateSpotlight(), 300);
        } else {
            updateSpotlight();
        }

        function updateSpotlight() {
            if (!slide.targetId) {
                setSpotlightRect(null);
                return;
            }

            // Redirect nav items to hamburger menu on mobile if sidebar is closed
            let effectiveTargetId = slide.targetId;
            if (effectiveTargetId.startsWith('nav-item-')) {
                effectiveTargetId = 'mobile-menu-button';
            }

            const el = document.querySelector(`[data-tour="${effectiveTargetId}"]`) ||
                document.getElementById(effectiveTargetId);

            if (el) {
                setSpotlightRect(el.getBoundingClientRect());
            } else {
                setSpotlightRect(null);
            }
        }

        // Add resize listener
        window.addEventListener('resize', updateSpotlight);
        return () => window.removeEventListener('resize', updateSpotlight);
    }, [currentSlide, slide, logEvent, navigate, location.pathname]);

    const goNext = useCallback(() => {
        const slide = SLIDES[currentSlide];
        logEvent({ eventType: 'complete_slide', stepId: slide.id, platform: 'mobile' });

        if (isLastSlide) {
            logEvent({ eventType: 'complete_tour', stepId: 'all', platform: 'mobile' });
            onComplete();
        } else {
            setDirection(1);
            setCurrentSlide((prev) => prev + 1);
        }
    }, [currentSlide, isLastSlide, logEvent, onComplete]);

    const goPrev = useCallback(() => {
        if (currentSlide > 0) {
            setDirection(-1);
            setCurrentSlide((prev) => prev - 1);
        }
    }, [currentSlide]);

    const handleSkip = useCallback(() => {
        logEvent({ eventType: 'skip', stepId: SLIDES[currentSlide].id, platform: 'mobile' });
        onComplete();
    }, [currentSlide, logEvent, onComplete]);

    const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const threshold = 50;
        if (info.offset.x < -threshold) {
            goNext();
        } else if (info.offset.x > threshold) {
            goPrev();
        }
    };

    return (
        <div className="fixed inset-0 z-[10001] pointer-events-none" style={{ touchAction: 'none' }}>
            {/* Backdrop with Spotlight Mask */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                    <mask id="mobile-spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {spotlightRect && (
                            <motion.rect
                                initial={false}
                                animate={{
                                    x: spotlightRect.x - 4,
                                    y: spotlightRect.y - 4,
                                    width: spotlightRect.width + 8,
                                    height: spotlightRect.height + 8,
                                    rx: 8
                                }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.5)"
                    mask="url(#mobile-spotlight-mask)"
                    className="backdrop-blur-[1px] pointer-events-auto"
                    onClick={handleSkip}
                />
            </svg>

            {/* Bottom Sheet Container */}
            <div className="absolute bottom-0 left-0 right-0 p-4 pb-8 sm:pb-10 pointer-events-none">
                <div className="relative w-full max-w-md mx-auto pointer-events-auto">

                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentSlide}
                            custom={direction}
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.95 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.2}
                            onDragEnd={handleDragEnd}
                            className={cn(
                                "flex flex-col overflow-hidden rounded-[2rem] shadow-2xl bg-gradient-to-br cursor-grab active:cursor-grabbing",
                                slide.bgGradient
                            )}
                        >
                            {/* Header / Nav Area */}
                            <div className="px-6 pt-6 pb-2">
                                {/* Progress Bar */}
                                <div className="flex gap-1.5 mb-6">
                                    {SLIDES.map((_, idx) => (
                                        <div
                                            key={idx}
                                            className="flex-1 h-1 rounded-full overflow-hidden bg-white/30"
                                        >
                                            <motion.div
                                                className="h-full bg-white"
                                                initial={{ width: 0 }}
                                                animate={{
                                                    width: idx < currentSlide ? '100%' : idx === currentSlide ? '100%' : '0%',
                                                }}
                                                transition={{ duration: idx === currentSlide ? 0.3 : 0.3 }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-between items-start">
                                    {/* Icon */}
                                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white shadow-inner border border-white/10">
                                        {slide.icon}
                                    </div>

                                    {/* Skip Button */}
                                    <button
                                        onClick={handleSkip}
                                        className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider py-2 px-1"
                                    >
                                        Skip
                                    </button>
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="px-6 pb-8 pt-2">
                                {/* Title */}
                                <h2 className="text-2xl sm:text-3xl font-black text-white mb-3 leading-tight tracking-tight">
                                    {slide.title}
                                </h2>

                                {/* Description */}
                                <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-8 font-medium">
                                    {slide.description}
                                </p>

                                {/* CTA Button */}
                                <Button
                                    onClick={goNext}
                                    size="lg"
                                    className="w-full h-12 rounded-xl bg-white text-black font-bold text-sm shadow-xl hover:bg-white/90 active:scale-95 transition-all"
                                >
                                    {isLastSlide ? (
                                        <>
                                            Get Started <Check className="w-4 h-4 ml-2" />
                                        </>
                                    ) : (
                                        <>
                                            Continue <ChevronRight className="w-4 h-4 ml-1" />
                                        </>
                                    )}
                                </Button>

                                <p className="text-center text-white/40 text-[10px] uppercase tracking-widest mt-4 font-bold">
                                    Swipe to navigate
                                </p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
